import {track} from '@getmoss/moss-analytics'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {HeroV3SliceDefault} from 'prismicio-types'
import type {FC} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {RatingsSection} from 'slices/HeroV2/RatingsSection'
import {AssetsWrapper} from 'slices/HeroV3/components/AssetsWrapper'
import styles from 'slices/HeroV3/heroV3Styles.module.css'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {LogoBarV2} from 'slices/LogoBar/v2/LogoBarV2'
import {ButtonV2} from 'src/common/ButtonV2'
import {IconTag} from 'src/common/IconTag'
import {getButtonsData} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import type {Context} from 'src/common/types'

/**
 * Props for `HeroV3`.
 */
export type HeroV3Props = {
  slice: HeroV3SliceDefault & {
    slice_type: string
    variation: string
  }
  componentIndex: number
  context: Context
}

/**
 * Component for "HeroV3" Slices.
 */
export const HeroV3Default: FC<HeroV3Props> = ({slice, context, componentIndex}) => {
  const {ratings, eyebrow} = slice.primary
  const {linkResolver} = useLinkResolver()
  const logos = slice.primary.logos.map((logo) => logo.logo)
  const sanitizedHeading = sanitizeHtml(slice.primary.heading!)
  const hasRatings = isFilled.group(ratings) && ratings.length > 0
  const hasEyebrow = isFilled.group(eyebrow) && eyebrow.length > 0
  const domainCountry = useGetDomainCountry()

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    primaryButton: slice.primary.primary_button,
    buttonsData: context.buttonsData,
    secondaryButton: slice.primary.secondary_button,
  })

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-green-dark"
      data-nav-dark="true"
    >
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-5 pb-5 pt-[122px] md:px-10 md:pb-9 md:pt-[137px] lg:px-5 xl:px-0">
        {/* Content */}
        <div className="flex w-full flex-col items-center gap-6">
          {hasEyebrow && (
            <Link
              href={linkResolver(eyebrow[0].link)}
              onClick={() => {
                track({
                  event: 'interaction',
                  interaction: {
                    element: secondaryButtonData?.marketing_id || 'secondary-button-hero',
                    elementText: secondaryButtonData?.label || '',
                    location: `component-${componentIndex}-hero-v3`,
                    type: 'click',
                  },
                  page: {
                    type: context.pageUid,
                    domainCountry,
                  },
                })
              }}
              className={`group/button w-fit ${styles.tagBox} h-[38px]`}
            >
              <div className="relative z-10 flex size-full items-center justify-center gap-2 rounded-full bg-green-dark pl-2 pr-3">
                <div className="absolute inset-0 size-full rounded-full bg-moss-white/[10%] opacity-0 transition-opacity duration-300 ease-in-out group-hover/button:opacity-100" />
                <div className="flex size-6 items-center justify-center rounded-full">
                  <PrismicNextImage field={eyebrow[0].icon} className="size-4" />
                </div>
                <span className="text-nowrap text-15 font-medium leading-5 text-moss-white">{eyebrow[0]?.text}</span>
              </div>
            </Link>
          )}
          {hasRatings && <RatingsComponent ratings={ratings} />}
          <h1
            className="text-center text-[40px] font-normal leading-[42px] tracking-[-.4px] text-moss-white md:text-[72px] md:leading-[74px] md:tracking-[-.72px]"
            dangerouslySetInnerHTML={{__html: sanitizedHeading}}
          />
          <p className="text-tg-body-m md:text-tg-body-l max-w-[432px] text-center text-moss-white">
            {slice.primary.description}
          </p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            <ButtonV2
              className="uppercase"
              variant="primary"
              size="medium"
              trackingData={{
                location: `component-${componentIndex}-hero-v3`,
                dataType: 'primary-button',
                pageName: context.pageUid,
                mktgId: primaryButtonData?.marketing_id || '',
              }}
              href={linkResolver(primaryButtonData?.link, 'primary-button-hero')}
              newTrackingProps={{
                interaction: {
                  element: primaryButtonData?.marketing_id || 'primary-button-hero',
                  elementText: primaryButtonData?.label || '',
                  location: `component-${componentIndex}-hero-v3`,
                },
                pageType: context.pageUid,
              }}
            >
              {primaryButtonData?.label}
            </ButtonV2>
            {secondaryButtonData && (
              <ButtonV2
                className="uppercase"
                variant="secondary"
                size="medium"
                trackingData={{
                  location: `component-${componentIndex}-hero-v3`,
                  dataType: 'secondary-button',
                  pageName: context.pageUid,
                  mktgId: secondaryButtonData?.marketing_id || '',
                }}
                href={linkResolver(secondaryButtonData?.link, 'secondary-button-hero')}
                newTrackingProps={{
                  interaction: {
                    element: secondaryButtonData?.marketing_id || 'secondary-button-hero',
                    elementText: secondaryButtonData?.label || '',
                    location: `component-${componentIndex}-hero-v3`,
                  },
                  pageType: context.pageUid,
                }}
              >
                {secondaryButtonData?.label}
              </ButtonV2>
            )}
          </div>
          <AssetsWrapper assets={slice.primary.assets} />
          <LogoBarV2 title={slice.primary.logobar_text} logos={logos} />
        </div>
      </div>
    </section>
  )
}

const RatingsComponent = ({ratings}: {ratings: HeroV3SliceDefault['primary']['ratings']}) => {
  if (ratings.length > 1) {
    return <RatingsSection ratings={ratings} type="Animated" centered version="v2" />
  }

  return <IconTag iconName={ratings[0]?.review_platform} label={ratings[0]?.label!} version="v2" />
}
